import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { Button } from '@dru/ui';
import { api } from '../lib/api';
import { errorMessage } from '../lib/auth';
import { AuthFrame } from './AuthFrame';
import { CONTROL, ErrorNote, Field, GRADIENT_BTN, QUIET_LINK } from './Login';

/**
 * "I have forgotten my password."
 *
 * The API answers the same way whether or not the address has an account,
 * and this screen keeps that: the confirmation is worded for the person at
 * the keyboard and says nothing about whether anybody works here.
 */
export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.post('/auth/invitation/reset-request', { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthFrame
        title="Check your email"
        lead={<>If <strong>{email.trim().toLowerCase()}</strong> has a console account, a link to choose a new password is on its way. It works once and expires in one hour.</>}
      >
        <p className="text-caption font-normal text-neutral-600">
          Nothing arrived? Check the spam folder, make sure this is the work email your account
          was set up with, or ask your administrator to confirm the address.
        </p>
        <p className="mt-5 text-center">
          <Link to="/login" className={QUIET_LINK}>
            Back to sign in
          </Link>
        </p>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame
      title="Reset your password"
      lead="Enter your work email. We will send a link that lets you choose a new password yourself."
    >
      <form onSubmit={submit} noValidate>
        <Field label="Work email" icon={<Mail className="h-4 w-4" />}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
            autoFocus
            placeholder="you@company.com"
            className={CONTROL}
          />
        </Field>

        {error && <ErrorNote>{error}</ErrorNote>}

        <Button type="submit" loading={busy} disabled={!email.includes('@')} className={`mt-6 w-full ${GRADIENT_BTN}`}>
          Send me a link
        </Button>

        <p className="mt-4 text-center">
          <Link to="/login" className={QUIET_LINK}>
            Back to sign in
          </Link>
        </p>
      </form>
    </AuthFrame>
  );
}
